import boxOfferPlanIcon from "@images/illustrations/boxOfferPlanIcon.svg"
import { BoxOfferItemEntity } from "@modules/boxOffer/entities/boxOfferEntity"
import { CouponInputFormik, DurationType } from "@modules/boxSubscription/formik/boxSubscriptionFormik"
import { CouponType } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { word } from "@modules/core/utils/i18n"
import { useUserProfile } from "@modules/profile/publicProfile/hooks/useUserProfile"
import { MemberSendOfferModalDiscount } from "@modules/members/sendOfferToMember/components/memberSendOfferModalDiscount"
import { MemberSendOfferModalStartDate } from "@modules/members/sendOfferToMember/components/memberSendOfferModalStartDate"
import { MemberSendOfferModalStepOne } from "@modules/members/sendOfferToMember/components/memberSendOfferModalStepOne"
import { MemberSendOfferModalStepTwo } from "@modules/members/sendOfferToMember/components/memberSendOfferModalStepTwo"
import { HUText } from "@modules/ui/components/huText"
import { HUDialog } from "@modules/ui/components/popup/huDialog"
import { useCallback, useImperativeHandle, useState } from "react"
import { useTheme } from "styled-components"

export type MemberSendOfferModalRef = {
  open: () => void
}

type MemberSendOfferModalProps = {
  userId: string
  ref: React.Ref<MemberSendOfferModalRef>
}

export function MemberSendOfferModal({ userId, ref }: MemberSendOfferModalProps) {
  const theme = useTheme()
  const { data: user } = useUserProfile(userId)
  const [showModal, setShowModal] = useState(false)
  const [selectedOffer, setSelectedOffer] = useState<BoxOfferItemEntity | null>(null)
  const [step, setStep] = useState<number>(1)
  const [isDiscountVisible, setIsDiscountVisible] = useState(false)
  const [isDeferredDateVisible, setIsDeferredDateVisible] = useState(false)
  const discountCouponResetValue = {
    type: CouponType.PERCENT,
    value: 0,
    durationType: DurationType.UNLIMITED,
    months: null,
  }
  const [deferredDate, setDeferredDate] = useState<Date | null>(null)
  const [discountInput, setDiscountInput] = useState<CouponInputFormik>(discountCouponResetValue)

  const onHide = useCallback(() => {
    setShowModal(false)
    setIsDiscountVisible(false)
    setIsDeferredDateVisible(false)
    setSelectedOffer(null)
    setDiscountInput(discountCouponResetValue)
    setDeferredDate(null)
    setStep(1)
  }, [])

  const handleStep = () => {
    setStep((prev) => (prev === 1 ? 2 : 1))
    setDiscountInput(discountCouponResetValue)
    setDeferredDate(null)
  }

  useImperativeHandle(ref, () => ({
    open: () => {
      setShowModal(true)
    },
  }))

  if (!user) return null

  return (
    <HUDialog visible={showModal} header={""} onHide={onHide} style={{ width: "520px", height: "45rem" }}>
      {/* HEADER */}
      <div className="flex flex-column flex-wrap justify-content-center align-content-center text-center ">
        <div className=" flex gap-2 justify-content-center align-items-center">
          <img src={boxOfferPlanIcon} />
          <HUText fontStyle="TL" color={theme.mainWhite}>
            {word("box.offer.send.modal.title")}
          </HUText>
        </div>
        <HUText fontStyle="BM" color={theme.neutralColor600}>
          {user.firstname} {user.lastname}
        </HUText>
      </div>
      {/* STEPS */}
      {step === 1 && showModal && (
        <MemberSendOfferModalStepOne
          selectedOffer={selectedOffer}
          setSelectedOffer={setSelectedOffer}
          handleStep={handleStep}
          onHide={onHide}
        />
      )}
      {step === 2 && selectedOffer && !isDiscountVisible && !isDeferredDateVisible && (
        <MemberSendOfferModalStepTwo
          boxOfferId={selectedOffer.id}
          userId={user.userId}
          handleStep={handleStep}
          onHide={onHide}
          discountInput={discountInput}
          deferredDate={deferredDate}
          setIsDiscountVisible={setIsDiscountVisible}
          setIsDeferredDateVisible={setIsDeferredDateVisible}
        />
      )}
      {step === 2 && selectedOffer && isDiscountVisible && !isDeferredDateVisible && (
        <MemberSendOfferModalDiscount
          boxOfferId={selectedOffer.id}
          discountInput={discountInput}
          setDiscountInput={setDiscountInput}
          setIsDiscountVisible={setIsDiscountVisible}
        />
      )}
      {step === 2 && selectedOffer && !isDiscountVisible && isDeferredDateVisible && (
        <MemberSendOfferModalStartDate
          deferredDate={deferredDate}
          setDeferredDate={setDeferredDate}
          setIsDeferredDateVisible={setIsDeferredDateVisible}
        />
      )}
    </HUDialog>
  )
}
